import { useSpotlight } from '../api';
import { Scene3D } from '@/components/motion/Scene3D';

/**
 * The hero banner: whichever site currently holds the spotlight.
 *
 * Renders nothing when no site is spotlit — an empty state here would be a
 * conspicuous hole at the top of the page, and "no banner" is a normal
 * configuration, not an error.
 */
export function SpotlightBanner() {
  const { data: spotlight, isLoading } = useSpotlight();

  if (isLoading) {
    return (
      <div
        aria-hidden
        className="h-52 animate-pulse rounded-[var(--radius-card)] border border-surface-800 bg-surface-900 sm:h-64 sm:rounded-[1.75rem] lg:h-80"
      />
    );
  }

  if (!spotlight) {
    return null;
  }

  return (
    <section
      className="spotlight-hero relative overflow-hidden border border-gold-soft"
      aria-labelledby="spotlight-headline"
    >
      {spotlight.bannerUrl ? (
        <img
          src={spotlight.bannerUrl}
          alt=""
          // Decorative: the headline beside it already carries the meaning, so an
          // alt text here would just be read out twice.
          className="spotlight-art absolute inset-0 size-full object-cover object-right"
          loading="eager"
          // The hero is above the fold, so it is the one image worth fetching
          // at high priority.
          fetchPriority="high"
        />
      ) : null}

      {/* Left-weighted scrim: keeps the headline legible over whatever artwork
          an editor uploads, without dimming the artwork itself. */}
      <div className="absolute inset-0 bg-gradient-to-r from-surface-950 via-surface-950/85 to-transparent" />
      <Scene3D className="spotlight-scene" />

      {/*
        Taller than it looks like it needs to be, on purpose. The slot is as wide
        as the content column, so at the old 208px it was a 5:1 letterbox and
        `object-cover` threw away well over half the height of any banner an
        editor uploaded. At 320px a 2:1 source keeps roughly two thirds of its
        subject, which is what the artwork is drawn for.
      */}
      <div className="spotlight-copy relative flex flex-col justify-center gap-4">
        <span className="eyebrow">
          <span className="eyebrow-line" />
          Gee Family seçkisi
        </span>
        <h2 id="spotlight-headline" className="font-bold text-surface-50">
          {spotlight.headline}
        </h2>

        {spotlight.subline ? (
          <p className="max-w-md text-sm uppercase tracking-wide text-surface-300 sm:text-base">
            {spotlight.subline}
          </p>
        ) : null}

        <a
          href={`${spotlight.clickUrl}?from=hero`}
          target="_blank"
          rel="noreferrer noopener sponsored"
          className="gf-button gf-button-primary mt-2 w-fit"
        >
          {spotlight.callToAction}
          <ArrowGlyph />
        </a>
      </div>
    </section>
  );
}

function ArrowGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
      <path d="M13 5l7 7-7 7v-4H4v-6h9V5z" />
    </svg>
  );
}
